import React, { CSSProperties } from 'react';
import { Link, LinkProps, NavLinkProps, To } from 'react-router-dom';
import { applyLocaleToPath } from 'routing/paths';
import { useI18n } from '~/stores/i18n';

type ClassNameFn = (args: {
  isActive: boolean;
  isPending: boolean;
}) => string | undefined;

type ToWithState = {
  pathname: string;
  search?: string;
  hash?: string;
  state?: unknown;
};

type LinkComponent =
  | React.ComponentType<LinkProps>
  | React.ComponentType<NavLinkProps>;

interface LocalizedLinkProps extends Omit<LinkProps, 'to' | 'className'> {
  to: To;
  className?: string | ClassNameFn;
  activeClassName?: string;
  onClick?: (e: React.MouseEvent) => void;
  target?: string;
  ariaHidden?: boolean;
  title?: string;
  style?: CSSProperties;
  rel?: string;
  itemType?: string;
  itemProp?: string;
  'data-test-id'?: string;
  component?: LinkComponent;

  [key: string]: unknown;
}

const LocalizedLink = ({
  component: Component = Link,
  to,
  ariaHidden,
  className,
  activeClassName,
  ...rest
}: LocalizedLinkProps) => {
  const { lang: langCode } = useI18n();
  let localizedTo: To = '/';
  let stateProp: unknown = rest.state;

  if (typeof to === 'string') {
    localizedTo = applyLocaleToPath(to, langCode);
  } else if (typeof to === 'object') {
    const { state, pathname, search, hash } = to as ToWithState;
    stateProp = stateProp ?? state; // prefer explicit prop, fallback to legacy in to
    const toWithSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
    localizedTo = {
      pathname: applyLocaleToPath(toWithSlash, langCode),
      ...(search ? { search } : {}),
      ...(hash ? { hash } : {}),
    };
  }

  const computedClassName = activeClassName
    ? (nav: { isActive: boolean; isPending: boolean }) => {
        const base =
          typeof className === 'function' ? className(nav) : className;
        const active = nav.isActive ? activeClassName : '';
        return [base, active].filter(Boolean).join(' ') || undefined;
      }
    : className;

  const AnyComponent = Component as React.ComponentType<
    LinkProps | NavLinkProps
  >;

  return (
    <AnyComponent
      to={localizedTo}
      state={stateProp}
      aria-hidden={ariaHidden ? 'true' : null}
      className={computedClassName as NavLinkProps['className']}
      {...rest}
    />
  );
};

export default LocalizedLink;
